import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import HeroDiagram from "@/components/HeroDiagram";
import Marquee from "@/components/Marquee";
import AreaGlyph from "@/components/AreaGlyph";
import SectionHeader from "@/components/SectionHeader";
import FieldnotesForm from "@/components/FieldnotesForm";
import PatternCard from "@/components/PatternCard";
import { Reveal, MaskedLine, EASE } from "@/components/Reveal";
import { useTitle } from "@/hooks/use-title";
import {
  MARQUEE_ITEMS,
  EXPLORE_AREAS,
  MANIFESTO,
  IDEAS,
  SERVICES,
  WORK_OUTLINES,
  LEARN_PRODUCTS,
  HIDDEN_SLUGS,
} from "@/data/content";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const diagramY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const diagramOpacity = useTransform(scrollYProgress, [0, 0.9], [1, 0.1]);

  return (
    <section ref={ref} className="relative flex min-h-screen items-center overflow-hidden" data-testid="hero-section">
      <div className="bg-grid absolute inset-0 opacity-40" />
      <motion.div style={{ y: diagramY, opacity: diagramOpacity }} className="absolute inset-0">
        <HeroDiagram />
      </motion.div>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-ink via-ink/70 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-ink to-transparent" />

      <div className="wrap relative z-10 pb-28 pt-36 lg:pt-40">
        <motion.p
          className="kicker"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          data-testid="hero-kicker"
        >
          Operating Reality — Media · Education · Consulting
        </motion.p>
        <h1 className="mt-8 max-w-4xl text-5xl font-semibold leading-[1.05] tracking-tight text-slate-50 sm:text-6xl lg:text-7xl" data-testid="hero-headline">
          <MaskedLine delay={0.3}>See how work</MaskedLine>
          <MaskedLine delay={0.45}>
            actually <span className="text-glow">works.</span>
          </MaskedLine>
        </h1>
        <motion.p
          className="mt-8 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
          data-testid="hero-subcopy"
        >
          Operating Reality explores the hidden problems behind processes, people and
          technology — and what to change when the obvious solution isn't the real one.
        </motion.p>
        <motion.div
          className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1, ease: EASE }}
        >
          <Link to="/ideas" className="btn-primary" data-testid="hero-cta-explore">
            Explore the ideas <ArrowRight size={14} />
          </Link>
          <Link to="/consulting" className="btn-ghost" data-testid="hero-cta-work">
            Work with us
          </Link>
        </motion.div>
      </div>

      <motion.div
        className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        aria-hidden="true"
      >
        <span className="font-mono text-[9px] uppercase tracking-[0.4em] text-slate-600">Scroll</span>
        <span className="animate-scrollcue block h-10 w-px bg-glow/70" />
      </motion.div>
    </section>
  );
}

function Manifesto() {
  return (
    <section className="border-t border-line/50" data-testid="manifesto-section">
      <div className="wrap grid gap-12 py-24 lg:grid-cols-12 lg:py-36">
        <div className="lg:col-span-4">
          <Reveal>
            <p className="kicker">The position</p>
            <h2 className="mt-5 text-balance text-2xl font-medium leading-snug tracking-tight text-slate-100 lg:text-3xl">
              The problem is rarely where you first see it.
            </h2>
          </Reveal>
        </div>
        <div className="lg:col-span-8">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.n} delay={i * 0.12}>
              <div
                className="group flex items-baseline gap-6 border-t border-line/50 py-8 last:border-b lg:gap-10 lg:py-10"
                data-testid={`manifesto-chapter-${m.n}`}
              >
                <span className="font-mono text-xs tracking-[0.3em] text-glow/70">{m.n}</span>
                <p className="text-balance text-xl font-medium leading-snug tracking-tight text-slate-200 transition-colors duration-500 group-hover:text-white sm:text-2xl lg:text-[2rem] lg:leading-[1.3]">
                  {m.text}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Explore() {
  return (
    <section className="border-t border-line/50" data-testid="explore-section">
      <div className="wrap py-24 lg:py-32">
        <SectionHeader
          kicker="What we explore"
          title="Five places where the real problem hides."
        />
        <div className="mt-14 border-b border-line/50">
          {EXPLORE_AREAS.map((a, i) => (
            <Reveal key={a.id} delay={i * 0.06}>
              <div
                className="group grid grid-cols-[auto_1fr] items-center gap-5 border-t border-line/50 py-7 transition-colors duration-300 hover:bg-charcoal/40 sm:grid-cols-[auto_auto_1fr_auto] sm:gap-8 lg:py-8"
                data-testid={`explore-area-${a.id}`}
              >
                <span className="hidden font-mono text-xs tracking-[0.3em] text-slate-600 sm:block">{a.index}</span>
                <AreaGlyph type={a.glyph} className="h-10 w-10 text-slate-500 transition-colors duration-300 group-hover:text-glow" />
                <div>
                  <h3 className="font-mono text-sm uppercase tracking-[0.25em] text-slate-100 transition-colors duration-300 group-hover:text-glow">
                    {a.title}
                  </h3>
                  <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-400">{a.text}</p>
                </div>
                <ArrowUpRight
                  size={18}
                  className="hidden text-slate-700 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-glow sm:block"
                />
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Ideas() {
  return (
    <section className="border-t border-line/50" data-testid="ideas-section">
      <div className="wrap py-24 lg:py-32">
        <SectionHeader
          kicker="How Work Actually Works"
          title="The problems that keep appearing, regardless of the organisation or technology."
          link="/ideas"
          linkLabel="Explore the patterns"
          linkTestId="ideas-explore-all-link"
        />
        <div className="mt-14 grid gap-px border border-line/50 bg-line/50 md:grid-cols-2">
          {IDEAS.filter((i) => !HIDDEN_SLUGS.includes(i.slug)).slice(0, 4).map((idea, i) => (
            <Reveal key={idea.slug} delay={i * 0.07} className="bg-ink">
              <PatternCard idea={idea} testIdPrefix="idea-card" />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Learn() {
  return (
    <section className="border-t border-line/50 bg-charcoal/20" data-testid="learn-section">
      <div className="wrap grid gap-14 py-24 lg:grid-cols-12 lg:py-32">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="kicker">Learn</p>
            <h2 className="mt-5 text-balance text-3xl font-medium leading-[1.2] tracking-tight text-slate-100 lg:text-4xl">
              Understand the system before you change it.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 border border-line/60 bg-ink p-8 lg:p-10" data-testid="diagnostic-card">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-glow/80">
                Free diagnostic — 15 questions
              </p>
              <h3 className="mt-4 text-2xl font-medium tracking-tight text-slate-100">
                Operating Reality Diagnostic
              </h3>
              <p className="mt-3 max-w-lg text-sm leading-relaxed text-slate-400">
                Fifteen questions to help identify what's really broken before you automate it.
                Nothing leaves your browser — the diagnosis is yours.
              </p>
              <Link to="/diagnostic" className="btn-primary mt-8" data-testid="diagnostic-cta">
                Take the Diagnostic <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.15}>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
              In the workshop
            </p>
            <ul className="mt-6 divide-y divide-line/50 border-y border-line/50">
              {LEARN_PRODUCTS.map((p) => (
                <li key={p.title} className="flex items-center justify-between gap-4 py-5" data-testid={`learn-product-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <span className="text-sm font-medium text-slate-300">{p.title}</span>
                  <span className="shrink-0 font-mono text-[9px] uppercase tracking-[0.25em] text-slate-600">
                    {p.status}
                  </span>
                </li>
              ))}
            </ul>
            <Link
              to="/learn"
              className="group mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400 transition-colors duration-300 hover:text-glow"
              data-testid="learn-more-link"
            >
              More about Learn
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Consulting() {
  return (
    <section className="border-t border-line/50" data-testid="consulting-section">
      <div className="wrap py-24 lg:py-32">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="kicker">Consulting</p>
              <h2 className="mt-5 text-balance text-3xl font-medium leading-[1.2] tracking-tight text-slate-100 lg:text-4xl">
                When the technology isn't the problem.
              </h2>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400">
                Organisations often know that something isn't working. The instinct is to buy
                another tool. Sometimes the real problem is somewhere else.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                <Link to="/contact" className="btn-primary" data-testid="consulting-cta-conversation">
                  Start a conversation <ArrowRight size={14} />
                </Link>
                <Link to="/consulting" className="btn-ghost" data-testid="consulting-cta-explore">
                  Explore consulting
                </Link>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <ul className="divide-y divide-line/50 border-y border-line/50">
                {SERVICES.map((s) => (
                  <li key={s.id} className="flex items-baseline gap-4 py-4" data-testid={`home-service-${s.id}`}>
                    <span className="font-mono text-[10px] tracking-[0.25em] text-glow/70">{s.index}</span>
                    <span className="font-mono text-xs uppercase tracking-[0.2em] text-slate-300">{s.title}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function Work() {
  return (
    <section className="border-t border-line/50 bg-charcoal/20" data-testid="work-section">
      <div className="wrap py-24 lg:py-32">
        <SectionHeader
          kicker="Selected work"
          title="Transformation, documented honestly."
          link="/work"
          linkLabel="View selected work"
          linkTestId="work-view-all-link"
        />
        <div className="mt-14 grid gap-px border border-line/50 bg-line/50 sm:grid-cols-2 lg:grid-cols-3">
          {WORK_OUTLINES.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.05} className="bg-ink">
              <Link
                to={w.href || "/work"}
                className="group flex h-full flex-col justify-between gap-8 p-7 transition-colors duration-300 hover:bg-charcoal/50"
                data-testid={`work-card-${w.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-[0.28em] text-slate-600">{w.scope}</p>
                  <h3 className="mt-4 text-lg font-medium leading-snug tracking-tight text-slate-200 transition-colors duration-300 group-hover:text-white">
                    {w.title}
                  </h3>
                </div>
                <p className={`font-mono text-[9px] uppercase tracking-[0.25em] ${w.href ? "text-glow/70" : "text-slate-600"}`}>
                  {w.href ? "Read the case study →" : "Full case study — in preparation"}
                </p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Fieldnotes() {
  return (
    <section id="fieldnotes" className="relative overflow-hidden border-t border-line/50" data-testid="fieldnotes-section">
      <div className="bg-grid absolute inset-0 opacity-25" />
      <div className="pointer-events-none absolute -right-40 top-1/2 h-96 w-96 -translate-y-1/2 rounded-full bg-signal/10 blur-[120px]" />
      <div className="wrap relative grid gap-12 py-24 lg:grid-cols-12 lg:py-32">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="kicker">Email</p>
            <h2 className="mt-5 text-balance text-3xl font-medium leading-[1.2] tracking-tight text-slate-100 lg:text-4xl">
              Operating Reality Fieldnotes
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-400">
              One practical observation about how organisations actually work.
            </p>
            <p className="mt-3 max-w-lg text-base leading-relaxed text-slate-400">
              No hype. No generic productivity advice. Just useful ideas about processes,
              technology and transformation.
            </p>
          </Reveal>
        </div>
        <div className="flex items-center lg:col-span-5">
          <Reveal delay={0.1} className="w-full">
            <FieldnotesForm />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ClosingCta() {
  return (
    <section className="border-t border-line/50" data-testid="closing-cta-section">
      <div className="wrap py-24 lg:py-36">
        <Reveal className="max-w-3xl">
          <p className="kicker">Consulting</p>
          <h2 className="mt-6 text-balance text-4xl font-semibold leading-[1.1] tracking-tight text-slate-50 sm:text-5xl">
            Have a problem you can't quite explain?
          </h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg">
            If you know something isn't working but aren't sure whether the problem is process,
            people, technology or governance — let's investigate it.
          </p>
          <Link to="/contact" className="btn-primary mt-10" data-testid="closing-cta-conversation">
            Start a conversation <ArrowRight size={14} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  useTitle("Operating Reality — See how work actually works.");
  return (
    <main data-testid="home-page">
      <Hero />
      <Manifesto />
      <Marquee items={MARQUEE_ITEMS} />
      <Explore />
      <Ideas />
      <Learn />
      <Consulting />
      <Work />
      <Fieldnotes />
      <ClosingCta />
    </main>
  );
}
